import { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, Check, MessageCircle, Zap, Percent, Headphones, Crown, Bell, Trophy } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function labelFor({ has, busy, defaultText, activeText }) {
  if (has) return activeText;
  if (busy) return "Redirecting…";
  return defaultText;
}

export default function Membership() {
  const [cfg, setCfg] = useState(null);
  const { user, token, authFetch } = useAuth();
  const nav = useNavigate();
  const [busyPriority, setBusyPriority] = useState(false);
  const [busyFeatured, setBusyFeatured] = useState(false);

  useEffect(() => {
    axios.get(`${API}/config`).then((r) => setCfg(r.data));
  }, []);

  const subscribe = async ({ lookup_key, kind, setBusy }) => {
    if (!token) {
      toast.info("Sign in to subscribe");
      nav("/login?next=/membership");
      return;
    }
    setBusy(true);
    try {
      const r = await authFetch({
        method: "POST",
        url: `${API}/payments/checkout`,
        data: { lookup_key, quantity: 1, origin_url: window.location.origin, kind },
      });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
      setBusy(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-5 md:px-8 pt-14 md:pt-20 pb-24">
      <div className="text-center max-w-2xl mx-auto">
        <span className="label-eyebrow">plans for both sides</span>
        <h1 className="font-display text-5xl md:text-6xl font-bold mt-4 leading-tight">
          Free or <span className="italic" style={{ color: "var(--secondary)" }}>fast-tracked</span>.
        </h1>
        <p className="mt-5 text-[color:var(--text-muted)]">
          Priority is for people who need help. Featured is for Mates who want to be found first.
        </p>
      </div>

      {/* Priority (customer) tier */}
      <div className="mt-14">
        <div className="mb-4 label-eyebrow">for customers · "i need help"</div>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="rounded-3xl border border-black/10 bg-white p-8">
            <div className="label-eyebrow">standard</div>
            <div className="mt-4 font-display text-5xl">₹0</div>
            <div className="text-sm text-neutral-500">Pay per booking</div>
            <ul className="mt-6 space-y-2.5 text-sm">
              {["Book any of 13 Assists", "Verified human Mates", "Standard match time"].map((x) => (
                <li key={x} className="flex gap-2"><Check className="w-4 h-4 text-neutral-500 shrink-0 mt-0.5" /> {x}</li>
              ))}
            </ul>
          </div>
          <div className="relative rounded-3xl border-2 overflow-hidden text-white p-8" style={{ borderColor: "var(--primary)", background: "var(--primary)" }}>
            <div className="grain absolute inset-0 opacity-25" />
            <div className="relative">
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5" style={{ color: "var(--secondary)" }} />
                <span className="text-xs uppercase tracking-widest font-bold" style={{ color: "var(--secondary)" }}>Priority</span>
              </div>
              <div className="mt-4 font-display text-5xl">₹{cfg?.membership?.price_inr || 99}</div>
              <div className="text-sm text-white/70">Per month · cancel anytime</div>
              <ul className="mt-6 space-y-2.5 text-sm">
                {[
                  { i: MessageCircle, t: "Instant WhatsApp connect" },
                  { i: Zap, t: "Priority match — under 10 min" },
                  { i: Percent, t: "10% off every booking" },
                  { i: Headphones, t: "Dedicated weekday concierge" },
                ].map(({ i: I, t }) => (
                  <li key={t} className="flex gap-2 items-start">
                    <I className="w-4 h-4 shrink-0 mt-0.5" style={{ color: "var(--secondary)" }} />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
              <button
                data-testid="subscribe-membership-btn"
                onClick={() => subscribe({ lookup_key: "sub_priority_monthly", kind: "membership", setBusy: setBusyPriority })}
                disabled={busyPriority || user?.is_member}
                className="w-full mt-8 py-3 rounded-full font-bold hover:opacity-90 transition-opacity disabled:opacity-70"
                style={{ background: "var(--secondary)", color: "#fff" }}
              >
                {labelFor({ has: !!user?.is_member, busy: busyPriority, defaultText: "Subscribe · ₹99/mo", activeText: "You're a Priority Member 🎉" })}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Featured Mate tier */}
      <div className="mt-14">
        <div className="mb-4 label-eyebrow">for helpers · "i want to help"</div>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="rounded-3xl border border-black/10 bg-white p-8">
            <div className="label-eyebrow">standard mate</div>
            <div className="mt-4 font-display text-5xl">Free</div>
            <div className="text-sm text-neutral-500">List and earn per job</div>
            <ul className="mt-6 space-y-2.5 text-sm">
              {["Standard listing in your city", "Keep 67% of every booking", "Rated after every task"].map((x) => (
                <li key={x} className="flex gap-2"><Check className="w-4 h-4 text-neutral-500 shrink-0 mt-0.5" /> {x}</li>
              ))}
            </ul>
          </div>
          <div className="relative rounded-3xl border-2 overflow-hidden text-white p-8" style={{ borderColor: "var(--secondary)", background: "var(--secondary)" }}>
            <div className="grain absolute inset-0 opacity-25" />
            <div className="relative">
              <div className="flex items-center gap-2">
                <Crown className="w-5 h-5 text-white" />
                <span className="text-xs uppercase tracking-widest font-bold text-white">Featured</span>
              </div>
              <div className="mt-4 font-display text-5xl">₹{cfg?.featured_mate?.price_inr || 199}</div>
              <div className="text-sm text-white/80">Per month · cancel anytime</div>
              <ul className="mt-6 space-y-2.5 text-sm">
                {[
                  { i: Trophy, t: "Top of every search in your city" },
                  { i: Crown, t: "Verified · Featured badge on profile" },
                  { i: Bell, t: "Priority job alerts (before Free Mates)" },
                  { i: Sparkles, t: "Featured on the login & landing page" },
                ].map(({ i: I, t }) => (
                  <li key={t} className="flex gap-2 items-start">
                    <I className="w-4 h-4 shrink-0 mt-0.5 text-white" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
              <button
                data-testid="subscribe-featured-btn"
                onClick={() => subscribe({ lookup_key: "sub_featured_mate_monthly", kind: "featured_mate", setBusy: setBusyFeatured })}
                disabled={busyFeatured || user?.is_featured_mate}
                className="w-full mt-8 py-3 rounded-full font-bold hover:opacity-90 transition-opacity disabled:opacity-70"
                style={{ background: "#fff", color: "var(--secondary)" }}
              >
                {labelFor({ has: !!user?.is_featured_mate, busy: busyFeatured, defaultText: "Become Featured · ₹199/mo", activeText: "You're Featured 🎉" })}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
